import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const IMPORTANCE_LABEL = { nice_to_have: "Nice to have", important: "Important", very_important: "Very important", deal_breaker: "Deal-breaker" };

// Offers to copy a confirmed Realisation into the matching profile answer. Nothing is saved without a click.
export function SaveRealisationToProfile({ realisationId, importance, disabled }) {
  const [s, setS] = useState(null);
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setDone(null); setReplace(false); setError(null);
    api.get(`/realisations/${realisationId}/profile-suggestion`)
      .then(({ data }) => { if (live) setS(data); })
      .catch(() => { if (live) setS(null); });
    return () => { live = false; };
  }, [realisationId, importance]);

  if (!s?.available) return null;
  const tid = `realisation-profile-${realisationId}`;
  if (done) {
    return <p data-testid={`${tid}-saved`} className="mt-3 text-sm text-emerald-800">Saved to your profile: {s.label} is now {done.proposed}/10 (from Realisations).</p>;
  }
  if (s.already_matches) {
    return <p data-testid={`${tid}-matches`} className="mt-3 text-sm text-stone-500">Your profile already says {s.current}/10 for this.</p>;
  }
  const save = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.post(`/realisations/${realisationId}/apply-to-profile`, { replace });
      setDone(data);
      window.dispatchEvent(new CustomEvent("wua:profile-updated"));
    } catch {
      setError("Couldn't save to your profile. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid={tid} className="mt-4 rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm text-stone-700">
      <p>
        <span className="font-semibold">Save to my profile?</span> {s.label}:{" "}
        <span data-testid={`${tid}-preview`}>
          {s.current_is_stated ? <>{s.current}/10 → <strong>{s.proposed}/10</strong></> : <>not set → <strong>{s.proposed}/10</strong></>}
        </span>{" "}
        (from "{IMPORTANCE_LABEL[s.importance] || s.importance}").
      </p>
      {s.current_is_stated && (
        <label className="mt-2 flex items-center gap-2">
          <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid={`${tid}-replace`} />
          Replace my current answer ({s.current}/10)
        </label>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <button type="button" onClick={save} disabled={disabled || busy || (s.current_is_stated && !replace)} data-testid={`${tid}-save`}
          className="inline-flex min-h-[40px] items-center rounded-md bg-[#4C1D95] px-3 text-sm font-medium text-white disabled:opacity-50">
          {busy ? "Saving…" : "Save to my profile"}
        </button>
        <span className="text-[12px] text-stone-500">Labelled as coming from Realisations. You can change it on your profile any time.</span>
      </div>
      {error && <p role="alert" className="mt-2 text-rose-700" data-testid={`${tid}-error`}>{error}</p>}
    </div>
  );
}
